import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, map, catchError, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import type { SubscriptionDTO } from '../core/models';
import type { WrapperPageableResponse } from './service-common';

const GROUP_DETAIL_PAGE_SIZE = 200;

export type TimelineGroupMode = 'day' | 'month';

export interface SubscriptionTimelineGroupSummary {
  date: string;
  renewalCount: number;
  totalAmount: number;
}

interface TimelineGroupApi {
  date?: string;
  renewalCount?: number;
  totalAmount?: number;
}

interface GroupDetailMeta {
  page: number;
  totalItems: number;
  hasMore: boolean;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionTimelineService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  readonly groupMode = signal<TimelineGroupMode>('day');
  readonly collapsedGroups = signal<SubscriptionTimelineGroupSummary[]>([]);
  readonly expandedGroups = signal<Set<string>>(new Set());
  readonly groupDetails = signal<Map<string, SubscriptionDTO[]>>(new Map());
  readonly groupDetailMeta = signal<Map<string, GroupDetailMeta>>(new Map());
  readonly groupLoading = signal<Set<string>>(new Set());
  readonly groupLoadingMore = signal<Set<string>>(new Set());
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly page = signal(0);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly pageSize = 20;

  reset(): void {
    this.collapsedGroups.set([]);
    this.expandedGroups.set(new Set());
    this.groupDetails.set(new Map());
    this.groupDetailMeta.set(new Map());
    this.groupLoading.set(new Set());
    this.groupLoadingMore.set(new Set());
    this.page.set(0);
    this.totalItems.set(0);
    this.totalPages.set(0);
  }

  setGroupMode(mode: TimelineGroupMode): void {
    if (this.groupMode() === mode) return;
    this.groupMode.set(mode);
    this.reset();
  }

  hasMore(): boolean {
    const totalPages = this.totalPages();
    if (totalPages > 0) {
      return this.page() + 1 < totalPages;
    }
    return this.collapsedGroups().length < this.totalItems();
  }

  isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups().has(groupKey);
  }

  isGroupLoading(groupKey: string): boolean {
    return this.groupLoading().has(groupKey);
  }

  isGroupLoadingMore(groupKey: string): boolean {
    return this.groupLoadingMore().has(groupKey);
  }

  hasMoreGroupItems(groupKey: string): boolean {
    return this.groupDetailMeta().get(groupKey)?.hasMore ?? false;
  }

  getGroupItems(groupKey: string): SubscriptionDTO[] {
    return this.groupDetails().get(groupKey) ?? [];
  }

  toggleGroup(groupKey: string): void {
    if (this.isGroupExpanded(groupKey)) {
      this.collapseGroup(groupKey);
      return;
    }
    this.expandGroup(groupKey);
  }

  collapseGroup(groupKey: string): void {
    const next = new Set(this.expandedGroups());
    next.delete(groupKey);
    this.expandedGroups.set(next);
  }

  expandGroup(groupKey: string): void {
    const next = new Set(this.expandedGroups());
    next.add(groupKey);
    this.expandedGroups.set(next);

    if (this.groupDetails().has(groupKey) || this.isGroupLoading(groupKey)) {
      return;
    }
    this.loadGroupDetail(groupKey, false).subscribe();
  }

  loadMoreGroupItems(groupKey: string): void {
    if (!this.isGroupExpanded(groupKey)) return;
    if (this.isGroupLoading(groupKey) || this.isGroupLoadingMore(groupKey)) return;
    if (!this.hasMoreGroupItems(groupKey)) return;
    this.loadGroupDetail(groupKey, true).subscribe();
  }

  loadCollapsedGroups(append: boolean): Observable<WrapperPageableResponse<SubscriptionTimelineGroupSummary>> {
    if (append) {
      this.loadingMore.set(true);
    } else {
      this.reset();
      this.loading.set(true);
    }

    const page = append ? this.page() + 1 : 0;
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(this.pageSize));

    if (this.groupMode() === 'month') {
      params = params.set('groupBy', 'month');
    }

    return this.http
      .get<WrapperPageableResponse<TimelineGroupApi>>(`${this.apiUrl}/subscriptions/timeline`, { params })
      .pipe(
        map((response) => ({
          ...response,
          body: (response.body ?? []).map((row) => mapTimelineGroup(row, this.groupMode()))
        })),
        tap({
          next: (response) => {
            const batch = response.body ?? [];
            if (append) {
              this.collapsedGroups.set([...this.collapsedGroups(), ...batch]);
            } else {
              this.collapsedGroups.set(batch);
            }
            if (response.page) {
              this.totalItems.set(response.page.totalItems ?? batch.length);
              this.totalPages.set(response.page.totalPages ?? 0);
            } else {
              this.totalItems.set(batch.length);
              this.totalPages.set(batch.length > 0 ? 1 : 0);
            }
            this.page.set(page);
            this.loading.set(false);
            this.loadingMore.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.loadingMore.set(false);
          }
        })
      );
  }

  loadGroupDetail(groupKey: string, append: boolean): Observable<SubscriptionDTO[]> {
    if (append) {
      const loadingMore = new Set(this.groupLoadingMore());
      loadingMore.add(groupKey);
      this.groupLoadingMore.set(loadingMore);
    } else {
      const loading = new Set(this.groupLoading());
      loading.add(groupKey);
      this.groupLoading.set(loading);
    }

    const currentMeta = this.groupDetailMeta().get(groupKey);
    const page = append ? (currentMeta?.page ?? 0) + 1 : 0;
    let params = new HttpParams()
      .set('on', groupKey)
      .set('page', String(page))
      .set('size', String(GROUP_DETAIL_PAGE_SIZE))
      .set('sort', 'nextBillingDate,asc');

    if (this.groupMode() === 'month') {
      params = params.set('groupBy', 'month');
    }

    return this.http
      .get<WrapperPageableResponse<SubscriptionDTO>>(`${this.apiUrl}/subscriptions/timeline/occurrences`, { params })
      .pipe(
        map((response) => {
          const items = response.ok ? (response.body ?? []) : [];
          const totalItems = response.page?.totalItems ?? items.length;
          const totalPages = response.page?.totalPages ?? (items.length > 0 ? 1 : 0);
          const hasMore = totalPages > 0 ? page + 1 < totalPages : items.length >= GROUP_DETAIL_PAGE_SIZE;

          const details = new Map(this.groupDetails());
          const existing = append ? (details.get(groupKey) ?? []) : [];
          details.set(groupKey, [...existing, ...items]);
          this.groupDetails.set(details);

          const meta = new Map(this.groupDetailMeta());
          meta.set(groupKey, { page, totalItems, hasMore });
          this.groupDetailMeta.set(meta);

          this.clearGroupLoading(groupKey);
          return details.get(groupKey) ?? [];
        }),
        catchError((err) => {
          this.clearGroupLoading(groupKey);
          return throwError(() => err);
        })
      );
  }

  private clearGroupLoading(groupKey: string): void {
    const loading = new Set(this.groupLoading());
    loading.delete(groupKey);
    this.groupLoading.set(loading);

    const loadingMore = new Set(this.groupLoadingMore());
    loadingMore.delete(groupKey);
    this.groupLoadingMore.set(loadingMore);
  }
}

function mapTimelineGroup(row: TimelineGroupApi, mode: TimelineGroupMode): SubscriptionTimelineGroupSummary {
  const rawDate = (row.date ?? '').slice(0, 10);
  const date = mode === 'month' ? rawDate.slice(0, 7) : rawDate;
  return {
    date,
    renewalCount: Number(row.renewalCount ?? 0),
    totalAmount: Number(row.totalAmount ?? 0)
  };
}
